
import { AIModule } from '../../types';

export const MLOPS_MODULE_2: AIModule = {
  id: 'cicd-for-ml',
  title: 'CI/CD for Machine Learning',
  description: 'Implement continuous integration and deployment pipelines specifically designed for machine learning systems.',
  duration: '4 hours',
  difficulty: 'medium',
  content: `
# CI/CD for Machine Learning

Continuous Integration and Continuous Deployment (CI/CD) for machine learning systems requires specialized approaches beyond traditional software development. This module covers ML-specific CI/CD practices.

## Understanding ML CI/CD Challenges

### Traditional Software vs ML Systems

**Traditional Software:**
- Code changes are the primary concern
- Deterministic outputs
- Version control focuses on code
- Testing is primarily functional

**ML Systems:**
- Code, data, and model changes
- Non-deterministic outputs
- Version control includes data and models
- Testing includes performance metrics

### Key Components of ML CI/CD

1. **Data Validation**
2. **Model Training Pipeline**
3. **Model Validation**
4. **Model Deployment**
5. **Monitoring and Rollback**

## Implementing ML CI/CD Pipeline

\`\`\`python
import os
import json
import joblib
import pandas as pd
import numpy as np
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import accuracy_score, classification_report
import mlflow
import mlflow.sklearn
from typing import Dict, Tuple, Any
import logging
from dataclasses import dataclass
from datetime import datetime
import subprocess
import yaml

# Configure logging
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

@dataclass
class MLConfig:
    """Configuration for ML pipeline"""
    model_name: str
    version: str
    data_path: str
    model_path: str
    metrics_threshold: Dict[str, float]
    deployment_target: str
    
    @classmethod
    def from_yaml(cls, config_path: str) -> 'MLConfig':
        with open(config_path, 'r') as f:
            config = yaml.safe_load(f)
        return cls(**config)

class DataValidator:
    """Validate data quality and schema"""
    
    def __init__(self, expected_schema: Dict[str, str]):
        self.expected_schema = expected_schema
    
    def validate_schema(self, df: pd.DataFrame) -> bool:
        """Validate dataframe schema"""
        try:
            # Check column names
            expected_columns = set(self.expected_schema.keys())
            actual_columns = set(df.columns)
            
            if expected_columns != actual_columns:
                missing = expected_columns - actual_columns
                extra = actual_columns - expected_columns
                logger.error(f"Schema mismatch. Missing: {missing}, Extra: {extra}")
                return False
            
            # Check data types
            for col, expected_type in self.expected_schema.items():
                if str(df[col].dtype) != expected_type:
                    logger.error(f"Type mismatch for {col}: expected {expected_type}, got {df[col].dtype}")
                    return False
            
            logger.info("Schema validation passed")
            return True
            
        except Exception as e:
            logger.error(f"Schema validation failed: {e}")
            return False
    
    def validate_data_quality(self, df: pd.DataFrame) -> Dict[str, Any]:
        """Validate data quality metrics"""
        quality_report = {
            'total_rows': len(df),
            'missing_values': df.isnull().sum().to_dict(),
            'duplicate_rows': df.duplicated().sum(),
            'numerical_stats': df.describe().to_dict() if len(df.select_dtypes(include=[np.number]).columns) > 0 else {}
        }
        
        # Check for data quality issues
        issues = []
        
        # Check missing values
        missing_threshold = 0.1  # 10%
        for col, missing_count in quality_report['missing_values'].items():
            missing_rate = missing_count / len(df)
            if missing_rate > missing_threshold:
                issues.append(f"High missing values in {col}: {missing_rate:.2%}")
        
        # Check duplicates
        duplicate_threshold = 0.05  # 5%
        duplicate_rate = quality_report['duplicate_rows'] / len(df)
        if duplicate_rate > duplicate_threshold:
            issues.append(f"High duplicate rate: {duplicate_rate:.2%}")
        
        quality_report['issues'] = issues
        quality_report['passed'] = len(issues) == 0
        
        return quality_report

class ModelTrainer:
    """Handle model training with versioning"""
    
    def __init__(self, config: MLConfig):
        self.config = config
        mlflow.set_experiment(config.model_name)
    
    def train_model(self, X_train: pd.DataFrame, X_test: pd.DataFrame, 
                   y_train: pd.Series, y_test: pd.Series) -> Tuple[Any, Dict[str, float]]:
        """Train model with MLflow tracking"""
        
        with mlflow.start_run(run_name=f"{self.config.model_name}_v{self.config.version}"):
            # Log parameters
            mlflow.log_param("model_type", "RandomForest")
            mlflow.log_param("n_estimators", 100)
            mlflow.log_param("random_state", 42)
            mlflow.log_param("version", self.config.version)
            
            # Train model
            model = RandomForestClassifier(n_estimators=100, random_state=42)
            model.fit(X_train, y_train)
            
            # Make predictions
            y_pred = model.predict(X_test)
            
            # Calculate metrics
            accuracy = accuracy_score(y_test, y_pred)
            metrics = {
                'accuracy': accuracy,
                'training_samples': len(X_train),
                'test_samples': len(X_test)
            }
            
            # Log metrics
            for metric_name, metric_value in metrics.items():
                mlflow.log_metric(metric_name, metric_value)
            
            # Log model
            mlflow.sklearn.log_model(
                model, 
                "model",
                registered_model_name=self.config.model_name
            )
            
            # Save model locally
            os.makedirs(os.path.dirname(self.config.model_path), exist_ok=True)
            joblib.dump(model, self.config.model_path)
            
            logger.info(f"Model trained with accuracy: {accuracy:.4f}")
            return model, metrics

class ModelValidator:
    """Validate model performance and quality"""
    
    def __init__(self, config: MLConfig):
        self.config = config
    
    def validate_performance(self, metrics: Dict[str, float]) -> bool:
        """Validate model meets performance thresholds"""
        validation_passed = True
        
        for metric_name, threshold in self.config.metrics_threshold.items():
            if metric_name in metrics:
                actual_value = metrics[metric_name]
                if actual_value < threshold:
                    logger.error(f"Model failed {metric_name} threshold: {actual_value:.4f} < {threshold:.4f}")
                    validation_passed = False
                else:
                    logger.info(f"Model passed {metric_name} threshold: {actual_value:.4f} >= {threshold:.4f}")
            else:
                logger.warning(f"Metric {metric_name} not found in results")
        
        return validation_passed
    
    def validate_model_artifacts(self) -> bool:
        """Validate model artifacts exist and are valid"""
        try:
            # Check if model file exists
            if not os.path.exists(self.config.model_path):
                logger.error(f"Model file not found: {self.config.model_path}")
                return False
            
            # Try loading the model
            model = joblib.load(self.config.model_path)
            
            # Basic model validation
            if not hasattr(model, 'predict'):
                logger.error("Model does not have predict method")
                return False
            
            logger.info("Model artifacts validation passed")
            return True
            
        except Exception as e:
            logger.error(f"Model artifacts validation failed: {e}")
            return False

class DeploymentManager:
    """Handle model deployment"""
    
    def __init__(self, config: MLConfig):
        self.config = config
    
    def deploy_to_staging(self) -> bool:
        """Deploy model to staging environment"""
        try:
            logger.info("Deploying to staging environment...")
            
            # Create deployment package
            deployment_info = {
                'model_name': self.config.model_name,
                'version': self.config.version,
                'model_path': self.config.model_path,
                'timestamp': datetime.now().isoformat(),
                'target': 'staging'
            }
            
            # Save deployment info
            deployment_path = f"deployments/staging_{self.config.model_name}_v{self.config.version}.json"
            os.makedirs(os.path.dirname(deployment_path), exist_ok=True)
            
            with open(deployment_path, 'w') as f:
                json.dump(deployment_info, f, indent=2)
            
            logger.info(f"Model deployed to staging: {deployment_path}")
            return True
            
        except Exception as e:
            logger.error(f"Staging deployment failed: {e}")
            return False
    
    def deploy_to_production(self) -> bool:
        """Deploy model to production environment"""
        try:
            logger.info("Deploying to production environment...")
            
            # Additional production checks
            if not self.run_smoke_tests():
                logger.error("Smoke tests failed, aborting production deployment")
                return False
            
            # Create production deployment
            deployment_info = {
                'model_name': self.config.model_name,
                'version': self.config.version,
                'model_path': self.config.model_path,
                'timestamp': datetime.now().isoformat(),
                'target': 'production'
            }
            
            # Save deployment info
            deployment_path = f"deployments/production_{self.config.model_name}_v{self.config.version}.json"
            os.makedirs(os.path.dirname(deployment_path), exist_ok=True)
            
            with open(deployment_path, 'w') as f:
                json.dump(deployment_info, f, indent=2)
            
            logger.info(f"Model deployed to production: {deployment_path}")
            return True
            
        except Exception as e:
            logger.error(f"Production deployment failed: {e}")
            return False
    
    def run_smoke_tests(self) -> bool:
        """Run basic smoke tests on the model"""
        try:
            # Load model
            model = joblib.load(self.config.model_path)
            
            # Create dummy test data (in real scenario, use actual test data)
            dummy_data = np.random.rand(5, 4)  # Assuming 4 features
            
            # Test prediction
            predictions = model.predict(dummy_data)
            
            # Basic checks
            if predictions is None or len(predictions) == 0:
                logger.error("Model returned empty predictions")
                return False
            
            logger.info("Smoke tests passed")
            return True
            
        except Exception as e:
            logger.error(f"Smoke tests failed: {e}")
            return False

class MLPipeline:
    """Main ML CI/CD pipeline orchestrator"""
    
    def __init__(self, config_path: str):
        self.config = MLConfig.from_yaml(config_path)
        self.data_validator = DataValidator({
            'feature1': 'float64',
            'feature2': 'float64',
            'feature3': 'float64',
            'feature4': 'float64',
            'target': 'int64'
        })
        self.model_trainer = ModelTrainer(self.config)
        self.model_validator = ModelValidator(self.config)
        self.deployment_manager = DeploymentManager(self.config)
    
    def run_pipeline(self) -> bool:
        """Run the complete ML CI/CD pipeline"""
        try:
            logger.info("Starting ML CI/CD pipeline...")
            
            # Step 1: Data Validation
            logger.info("Step 1: Data validation")
            data = pd.read_csv(self.config.data_path)
            
            if not self.data_validator.validate_schema(data):
                logger.error("Data schema validation failed")
                return False
            
            quality_report = self.data_validator.validate_data_quality(data)
            if not quality_report['passed']:
                logger.error(f"Data quality validation failed: {quality_report['issues']}")
                return False
            
            # Step 2: Data Preparation
            logger.info("Step 2: Data preparation")
            X = data.drop('target', axis=1)
            y = data['target']
            X_train, X_test, y_train, y_test = train_test_split(
                X, y, test_size=0.2, random_state=42
            )
            
            # Step 3: Model Training
            logger.info("Step 3: Model training")
            model, metrics = self.model_trainer.train_model(X_train, X_test, y_train, y_test)
            
            # Step 4: Model Validation
            logger.info("Step 4: Model validation")
            if not self.model_validator.validate_performance(metrics):
                logger.error("Model performance validation failed")
                return False
            
            if not self.model_validator.validate_model_artifacts():
                logger.error("Model artifacts validation failed")
                return False
            
            # Step 5: Deployment to Staging
            logger.info("Step 5: Staging deployment")
            if not self.deployment_manager.deploy_to_staging():
                logger.error("Staging deployment failed")
                return False
            
            # Step 6: Production Deployment (if configured)
            if self.config.deployment_target == 'production':
                logger.info("Step 6: Production deployment")
                if not self.deployment_manager.deploy_to_production():
                    logger.error("Production deployment failed")
                    return False
            
            logger.info("ML CI/CD pipeline completed successfully!")
            return True
            
        except Exception as e:
            logger.error(f"Pipeline failed: {e}")
            return False

# GitHub Actions Integration
def create_github_workflow():
    """Create GitHub Actions workflow for ML CI/CD"""
    
    workflow = """
name: ML CI/CD Pipeline

on:
  push:
    branches: [ main, develop ]
  pull_request:
    branches: [ main ]

jobs:
  data-validation:
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v3
    - name: Set up Python
      uses: actions/setup-python@v3
      with:
        python-version: '3.9'
    - name: Install dependencies
      run: |
        pip install -r requirements.txt
    - name: Run data validation
      run: |
        python scripts/validate_data.py
    
  model-training:
    needs: data-validation
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v3
    - name: Set up Python
      uses: actions/setup-python@v3
      with:
        python-version: '3.9'
    - name: Install dependencies
      run: |
        pip install -r requirements.txt
    - name: Train model
      run: |
        python scripts/train_model.py
    - name: Upload model artifacts
      uses: actions/upload-artifact@v3
      with:
        name: model-artifacts
        path: models/
  
  model-validation:
    needs: model-training
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v3
    - name: Download model artifacts
      uses: actions/download-artifact@v3
      with:
        name: model-artifacts
        path: models/
    - name: Set up Python
      uses: actions/setup-python@v3
      with:
        python-version: '3.9'
    - name: Install dependencies
      run: |
        pip install -r requirements.txt
    - name: Validate model
      run: |
        python scripts/validate_model.py
  
  deploy-staging:
    needs: model-validation
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/develop'
    steps:
    - uses: actions/checkout@v3
    - name: Deploy to staging
      run: |
        python scripts/deploy_staging.py
  
  deploy-production:
    needs: model-validation
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/main'
    steps:
    - uses: actions/checkout@v3
    - name: Deploy to production
      run: |
        python scripts/deploy_production.py
"""
    
    return workflow

# Example usage and configuration
def create_sample_config():
    """Create sample configuration file"""
    
    config = {
        'model_name': 'iris_classifier',
        'version': '1.0.0',
        'data_path': 'data/iris.csv',
        'model_path': 'models/iris_classifier_v1.0.0.pkl',
        'metrics_threshold': {
            'accuracy': 0.85
        },
        'deployment_target': 'staging'
    }
    
    return config

# Example pipeline execution
def run_example_pipeline():
    """Run example ML CI/CD pipeline"""
    
    # Create sample configuration
    config = create_sample_config()
    
    # Save configuration
    with open('ml_config.yaml', 'w') as f:
        yaml.dump(config, f)
    
    # Create sample data
    from sklearn.datasets import load_iris
    iris = load_iris()
    df = pd.DataFrame(iris.data, columns=['feature1', 'feature2', 'feature3', 'feature4'])
    df['target'] = iris.target
    
    # Save sample data
    os.makedirs('data', exist_ok=True)
    df.to_csv('data/iris.csv', index=False)
    
    # Run pipeline
    pipeline = MLPipeline('ml_config.yaml')
    success = pipeline.run_pipeline()
    
    if success:
        print("✅ ML CI/CD pipeline completed successfully!")
    else:
        print("❌ ML CI/CD pipeline failed!")
    
    return success

# Run the example
if __name__ == "__main__":
    run_example_pipeline()
\`\`\`

## Advanced CI/CD Patterns

### 1. Blue-Green Deployment

Deploy new model version alongside current version and switch traffic gradually.

### 2. Canary Deployment

Deploy to small subset of users first to validate performance.

### 3. A/B Testing Integration

Built-in experimentation framework for model comparison.

### 4. Automated Rollback

Automatic rollback when performance degrades below threshold.

## Monitoring and Observability

### Model Performance Monitoring

- Prediction accuracy tracking
- Response time monitoring
- Resource utilization metrics
- Data drift detection

### Alerting System

- Performance degradation alerts
- Data quality issues
- Infrastructure problems
- Security incidents

This comprehensive CI/CD approach ensures reliable, automated deployment of ML models while maintaining quality and performance standards.
  `,
  quiz: {
    id: 'cicd-ml-quiz',
    title: 'CI/CD for Machine Learning Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What makes ML CI/CD different from traditional software CI/CD?',
        type: 'multiple-choice',
        options: [
          'ML systems only need code version control',
          'ML systems require versioning of code, data, and models',
          'ML systems are always deterministic',
          'ML systems do not need testing'
        ],
        correctAnswer: 1,
        explanation: 'ML systems require versioning and validation of code, data, and models, making CI/CD more complex than traditional software.',
        difficulty: 'medium',
        points: 20
      },
      {
        id: 'q2',
        question: 'What is the purpose of data validation in ML CI/CD?',
        type: 'multiple-choice',
        options: [
          'To check code syntax',
          'To verify data schema and quality before training',
          'To test user interface',
          'To validate model performance'
        ],
        correctAnswer: 1,
        explanation: 'Data validation ensures that incoming data meets expected schema and quality standards before model training.',
        difficulty: 'medium',
        points: 15
      },
      {
        id: 'q3',
        question: 'What deployment strategy gradually shifts traffic to a new model version?',
        type: 'multiple-choice',
        options: [
          'Blue-green deployment',
          'Canary deployment',
          'Rolling deployment',
          'Recreate deployment'
        ],
        correctAnswer: 1,
        explanation: 'Canary deployment gradually shifts a small percentage of traffic to the new model version to validate performance.',
        difficulty: 'medium',
        points: 15
      }
    ]
  },
  resources: [
    {
      id: 'mlops-cicd-guide',
      title: 'MLOps CI/CD Best Practices',
      type: 'article',
      url: 'https://example.com/mlops-cicd',
      description: 'Comprehensive guide to implementing CI/CD for machine learning systems'
    },
    {
      id: 'github-actions-ml',
      title: 'GitHub Actions for ML',
      type: 'documentation',
      url: 'https://docs.github.com/actions',
      description: 'Using GitHub Actions for ML CI/CD pipelines'
    }
  ]
};
